import type { ReactNode } from 'react';
import { Heading } from '../atoms/Heading';
import { Icon } from '../atoms/Icon';
import { Text } from '../atoms/Text';
import type { IconName } from '../icons/registry';
import styles from './EmptyState.module.css';

export interface EmptyStateProps {
  /** Small grey symbol above the title (like iOS "content unavailable"). */
  icon?: IconName;
  title: string;
  /** At most one sentence, only when the next step is not obvious. */
  description?: ReactNode;
  /** Usually one button to create the first item. */
  actions?: ReactNode;
  /** Heading level within the page outline. */
  level?: 1 | 2 | 3;
  /** `inline` for inside cards and lists, `page` for a whole page. */
  size?: 'inline' | 'page';
  /** Id of the heading, e.g. for `aria-labelledby`. */
  titleId?: string;
}

/** Says that something is empty and offers the next step (docs/ux-richtlinien.md §6.12). */
export function EmptyState({
  icon,
  title,
  description,
  actions,
  level = 2,
  size = 'inline',
  titleId,
}: EmptyStateProps) {
  return (
    <div className={`${styles.empty} ${styles[size]}`}>
      {icon && <Icon name={icon} size="xl" className={styles.icon} />}
      <div className={styles.text}>
        <Heading level={level} size={size === 'page' ? 'xl' : 'md'} id={titleId}>
          {title}
        </Heading>
        {description && (
          <Text size="sm" tone="muted">
            {description}
          </Text>
        )}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}
